import {
  Box,
  Button,
  Flex,
  HStack,
  IconArrow,
  IconButton,
  Show,
  useDisclosure,
} from '@gazin/ecommerce-ui'
import GImage from 'src/components/gImage'
import dynamic from 'next/dynamic'
import { useRef, useState } from 'react'
import { ThumborLoader } from 'src/util/thumborLoader'
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react'
import {
  Controller,
  FreeMode,
  Navigation,
  Pagination,
  Thumbs,
} from 'swiper/modules'

const ModalGallery = dynamic(
  () => import('../modalGallery').then((module) => module.ModalGallery),
  { ssr: false }
)

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/controller'
import 'swiper/css/thumbs'
import 'swiper/css/free-mode'
import 'swiper/swiper-bundle.css'
import {
  DefaultProductImageSwiperContainer,
  GallerySwiperContainer,
  ProductImageSwiperContainer,
} from './styles'

export type Image = {
  url: string
}

interface GalleryProps {
  images: Image[]
}

export const Gallery = ({ images }: GalleryProps) => {
  const gallerySwiperRef = useRef<any>(null)
  const thumbnailSwiperRef = useRef<any>(null)

  const [thumbsSwiper, setThumbsSwiper] = useState(null)

  const { isOpen, onOpen, onClose } = useDisclosure()
  const [slideIndex, setSlideIndex] = useState<number>(0)

  const getZoomImageUrl = (src: string) => ThumborLoader({ src })

  const [magnifyStyle, setMagnifyStyle] = useState({
    bg: `linear-gradient(rgba(54, 56, 67, 0.5), rgba(54, 56, 67, 0.5)), url('${getZoomImageUrl(
      images[0].url
    )}')`,
    bgPosition: 'center',
    hidden: true,
  })

  const [mouseBackgroundPoisitionOnHover, setMouseBackgroundPoisitionOnHover] =
    useState({
      mousePositionX: '0',
      mousePositionY: '0',
      bg: `linear-gradient(rgba(54, 56, 67, 0.5), rgba(54, 56, 67, 0.5))`,
      width: '8rem',
      height: '8rem',
      hidden: true,
      borderRadius: '50%',
      backgroundColor: 'transparente',
      border: '2px solid rgba(54, 56, 67, 0.8)',
    })

  const gallerySwiperParams: SwiperProps = {
    modules: [Pagination, Thumbs, Controller],
    slidesPerView: 1,
    effect: 'fade',
    pagination: {
      clickable: true,
    },
    slideToClickedSlide: true,
    onBeforeInit: (swiper: any) => {
      gallerySwiperRef.current = swiper
    },
    touchRatio: 0.2,
    thumbs: {
      swiper: thumbsSwiper,
    },
  }

  const thumbnailSwiperParams: SwiperProps = {
    modules: [FreeMode, Navigation, Thumbs, Controller],
    slidesPerView: 5.3,
    spaceBetween: 18,
    touchRatio: 0.2,
    onSwiper: setThumbsSwiper,
    centerInsufficientSlides: true,
    slideToClickedSlide: true,
    onBeforeInit: (swiper: any) => {
      thumbnailSwiperRef.current = swiper
    },
    freeMode: true,
    watchSlidesProgress: true,
    effect: 'fade',
    speed: 500,
  }

  function handleMouseMove(e: any, imageUrl: string) {
    const { offsetX, offsetY } = e.nativeEvent
    const { offsetWidth, offsetHeight } = e.nativeEvent.target as HTMLElement

    const circleSize = 80
    const halfCircleSize = circleSize / 2

    const xPercentage = ((offsetX / offsetWidth) * 100).toFixed(2)
    const yPercentage = ((offsetY / offsetHeight) * 100).toFixed(2)

    const zoomFactor = 2
    const bgSize = `${zoomFactor * 100}% ${zoomFactor * 100}%`

    setMouseBackgroundPoisitionOnHover({
      mousePositionX: `${offsetX - halfCircleSize}px`,
      mousePositionY: `${offsetY - halfCircleSize}px`,
      bg: `linear-gradient(rgba(54, 56, 67, 0.2), rgba(54, 56, 67, 0.2))`,
      width: `${circleSize}px`,
      height: `${circleSize}px`,
      hidden: false,
      borderRadius: '50%',
      backgroundColor: 'transparent',
      border: '2px solid rgba(54, 56, 67, 0.8)',
    })

    setMagnifyStyle((prev) => ({
      ...prev,
      bgPosition: `${xPercentage}% ${yPercentage}%`,
      bgSize: bgSize,
      hidden: false,
      bg: `url('${getZoomImageUrl(imageUrl)}')`,
    }))
  }

  function handleMouseLeave() {
    setMouseBackgroundPoisitionOnHover((prev) => ({
      ...prev,
      hidden: true,
    }))

    setMagnifyStyle((prev) => ({
      ...prev,
      hidden: true,
    }))
  }

  function handleOnClickImage(index: number) {
    setSlideIndex(index)
    onOpen()
  }

  if (images.length === 0) {
    return (
      <Box
        alignItems="center"
        border={{ lg: '1px solid #E3E3E8' }}
        display="flex"
        h={{ base: 'fit-content', lg: '26.052rem' }}
        justifyContent="center"
        pb={{ base: '1rem', lg: '0rem' }}
        position="relative"
        pt={{ base: '1rem', lg: '3.2rem' }}
        rounded="0.375rem"
        w={{ base: '100%', lg: '27.553rem' }}
        zIndex={0}
      >
        <Box maxW={{ base: '12.5rem', lg: '19.6rem' }} overflow="hidden">
          <Swiper
            ref={gallerySwiperRef}
            slidesPerView={1}
            effect="fade"
            pagination={{
              el: '.swiper-pagination',
              type: 'bullets',
              clickable: true,
            }}
            tag={DefaultProductImageSwiperContainer}
          >
            <Box
              cursor="pointer"
              w={{ base: '12.5rem', lg: '19.6rem' }}
              h={{ base: '13.5rem', lg: '19.75rem' }}
              m="0 auto"
            >
              <GImage
                src="/images/jpg/produto-default.jpg"
                alt="Produto sem Imagem"
                width={313.6}
                height={315.2}
                style={{
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Swiper>
        </Box>
      </Box>
    )
  }

  return (
    <Box w={{ base: '100%', lg: '27.553rem' }}>
      {isOpen && (
        <ModalGallery
          isOpen={isOpen}
          onClose={onClose}
          images={images}
          slideIndex={slideIndex}
        />
      )}

      <Flex
        alignItems="center"
        border={{ lg: '1px solid #E3E3E8' }}
        height={{ base: 'fit-content', lg: '26.052rem' }}
        position="relative"
        rounded="0.375rem"
        zIndex={1}
        overflow="hidden"
        justifyContent="center"
        paddingY={{ base: '1rem', lg: 'none' }}
      >
        <Box maxW={{ base: '12.5rem', lg: '27.553rem' }}>
          <Swiper
            {...gallerySwiperParams}
            ref={gallerySwiperRef}
            tag={ProductImageSwiperContainer}
          >
            {images?.map((image, index) => {
              return (
                <SwiperSlide key={`${image.url}-${index}`}>
                  <Box
                    cursor={{ base: 'pointer', lg: 'zoom-in' }}
                    height={{ base: '13.5rem', lg: '26.052rem' }}
                    onClick={() => handleOnClickImage(index)}
                    width={{ base: '12.5rem', lg: '27.553rem' }}
                  >
                    <GImage
                      src={image.url}
                      alt="Imagem do Produto"
                      sizes="27.553rem"
                      fill
                      priority={index === 0 ? true : false}
                      onMouseMove={(e: any) => handleMouseMove(e, image.url)}
                      onMouseLeave={handleMouseLeave}
                      style={{
                        objectFit: 'contain',
                        zIndex: 0,
                        borderRadius: '0.375rem',
                      }}
                    />
                  </Box>
                </SwiperSlide>
              )
            })}
          </Swiper>
        </Box>
        <Show above="lg">
          <Box
            bgRepeat="no-repeat"
            bgSize="100%"
            opacity={1}
            position="absolute"
            top={`${mouseBackgroundPoisitionOnHover.mousePositionY}`}
            left={`${mouseBackgroundPoisitionOnHover.mousePositionX}`}
            zIndex={3}
            transition="ease-in-out"
            pointerEvents="none"
            {...mouseBackgroundPoisitionOnHover}
          />
        </Show>
      </Flex>
      <Show above="lg">
        <Box
          border="2px #E8E8E8"
          padding="16px"
          boxShadow="0 4px 10px rgba(0, 0, 0, 0.2)"
          transform="translateY(-5px)"
          bgRepeat="no-repeat"
          height="32rem"
          width="37rem"
          opacity={1}
          position="absolute"
          left="28rem"
          top="0.3rem"
          zIndex={3}
          transition="ease-in-out"
          pointerEvents="none"
          rounded="0.375rem"
          {...magnifyStyle}
        />
      </Show>
      <HStack
        display={{ base: 'none', lg: 'flex' }}
        mt="0.6875rem"
        alignItems="center"
        justifyContent="center"
        position="relative"
        spacing={0}
      >
        <Swiper
          {...thumbnailSwiperParams}
          ref={thumbnailSwiperRef}
          tag={GallerySwiperContainer}
        >
          {images?.map((image, index) => {
            return (
              <SwiperSlide key={`${image.url}-${index}`}>
                <Button
                  w="3.875rem"
                  h="3.875rem"
                  bg="white"
                  border="1px solid #e3e3e8"
                  rounded="0.375rem"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  p="0.5rem"
                  _hover={{ bg: 'white' }}
                  transition="all 0.3s"
                >
                  <GImage
                    src={image.url}
                    alt="Imagem do Produto"
                    width={47}
                    height={47}
                    style={{ objectFit: 'contain' }}
                  />
                </Button>
              </SwiperSlide>
            )
          })}
        </Swiper>
        {images.length > 5 && (
          <>
            <IconButton
              icon={<IconArrow color="#0D71F0" transform="rotate(-180deg)" />}
              variant="unstyled"
              display={{ base: 'none', md: 'flex' }}
              justifyContent="flex-start"
              aria-label="prev-button"
              position="absolute"
              left="0"
              borderRadius="100%"
              zIndex={2}
              width="48px"
              height="48px"
              onClick={() => thumbnailSwiperRef.current?.slidePrev()}
            />
            <IconButton
              icon={<IconArrow color="#0D71F0" />}
              display={{ base: 'none', md: 'flex' }}
              justifyContent="flex-end"
              variant="unstyled"
              aria-label="next-button"
              position="absolute"
              right="0"
              zIndex={2}
              borderRadius="100%"
              width="48px"
              height="48px"
              onClick={() => thumbnailSwiperRef.current?.slideNext()}
            />
          </>
        )}
      </HStack>
    </Box>
  )
}
